import { MouseEvent, useCallback } from "react";

import { EmptyArray } from "../../utilities/EmptyArray";
import * as Color from "../Color";
import { DataId } from "../DataId";
import { Icon } from "../Icon";
import { BaseColumnProps } from "./BaseColumnProps";
import { TableLoader } from "./TableLoader";
import { useTableStyles } from "./TableStylesContext";
import { Custom } from "./columnTypes/Custom";
import { useHasChildContent } from "./useHasChildContent";
import { useIsChildRow } from "./useIsChildRow";

/** The `ExpandContractColumn` component properties. */
export interface ExpandContractColumnProps extends BaseColumnProps {
  /** The set of expanded row IDs. */
  readonly expandedIds?: ReadonlyArray<DataId>;

  /** The set of expanded IDs. */
  readonly expandedIdSet: ReadonlySet<DataId>;

  /** Called when the set of expanded row IDs are to be changed. */
  readonly onExpandedIdsChange?: (ids: ReadonlyArray<DataId>, previousIds: ReadonlyArray<DataId>) => void;
}

/** The `ExpandContractColumnContent` component properties. */
interface ExpandContractColumnContentProps {
  /** The row ID. */
  readonly rowId: DataId;

  /** Indicates whether the caret is currently in the expanded or contracted state. */
  readonly expanded: boolean;

  /** Called when the expand/contract state is being changed. */
  readonly onChange?: (rowId: DataId, expanded: boolean) => void;
}

/** The expand/contract column cell content. */
const ExpandContractColumnContent = (props: ExpandContractColumnContentProps) => {
  const { rowId, expanded, onChange } = props;

  const { styles } = useTableStyles();
  const isChildRow = useIsChildRow();
  const hasChildContent = useHasChildContent();

  const caretClickHandler = useCallback(
    (ev: MouseEvent<HTMLButtonElement>) => {
      ev.preventDefault();
      onChange?.(rowId, !expanded);
      ev.stopPropagation();
    },
    [rowId, expanded, onChange],
  );

  if (isChildRow || !hasChildContent) return null;

  return (
    <button
      className={styles.ExpandContractButton}
      onClick={caretClickHandler}
      aria-expanded={expanded}
      aria-label={expanded ? "Contract row" : "Expand row"}
    >
      {hasChildContent === "loading" && <TableLoader />}
      {hasChildContent !== "loading" && (
        <Icon name={expanded ? "chevron-down" : "chevron-right"} color={Color.Turqoise["500"]} aria-hidden="true" />
      )}
    </button>
  );
};

/** Column which handles expansion/contraction of expandable rows. */
export const ExpandContractColumn = (props: ExpandContractColumnProps) => {
  const { expandedIds = EmptyArray, expandedIdSet, onExpandedIdsChange } = props;

  const expandContractClickHandler = useCallback(
    (rowId: DataId, expanded: boolean) => {
      if (expanded && !expandedIdSet.has(rowId)) onExpandedIdsChange?.([...expandedIds, rowId], expandedIds);
      else if (!expanded && expandedIdSet.has(rowId))
        onExpandedIdsChange?.(
          expandedIds.filter((expandedId) => expandedId !== rowId),
          expandedIds,
        );
    },
    [expandedIds, expandedIdSet, onExpandedIdsChange],
  );

  const columnTemplateFunction = useCallback(
    (_: unknown, id: DataId) => (
      <ExpandContractColumnContent rowId={id} expanded={expandedIdSet.has(id)} onChange={expandContractClickHandler} />
    ),
    [expandedIds, onExpandedIdsChange, expandedIdSet],
  );

  return <Custom {...props}>{columnTemplateFunction}</Custom>;
};

ExpandContractColumn.className = "expand-contract";
